import React from 'react';
import {
    AppBar, Toolbar, Box, Container, Stack, Typography, Chip, Divider, Paper, Collapse, Button,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import OrderTimeline from '../OrderTimeLine';
import UserVerification from '../UserVerification';
import Action from '../Action';
import BrandBanner, { hasBrandBanner } from './shared/BrandBanner';
import PoweredBy from './shared/PoweredBy';
import SocialRow from './shared/SocialRow';
import CopyIconButton from './shared/CopyIconButton';
import PolicyLinks from './shared/PolicyLinks';
import TrackAnotherButton from './shared/TrackAnotherButton';
import {
    toAbsoluteUrl,
    getReadableAccent, getProgress, STEPS, arrivalLabel, formatDate, formatINR,
} from './trackingTemplateUtils';

const TONE = {
    accent: null,                 // resolved to brand accent at runtime
    success: '#1E8E5A',
    warning: '#E08600',
    error: '#C62828',
    neutral: '#98A2B3',
};

const INK = '#101828';
const SUB = '#475467';
const MUTE = '#98A2B3';
const HAIR = 'rgba(16,24,40,0.08)';

// Custom horizontal 4-stage progress tracker (full runtime-color control).
function ProgressTracker({ activeIdx, complete, color }) {
    const ratio = complete ? 1 : (STEPS.length > 1 ? activeIdx / (STEPS.length - 1) : 0);
    return (
        <Box sx={{ position: 'relative', mt: 1 }}>
            {/* base + filled connector lines, drawn between node centers (12.5% .. 87.5%) */}
            <Box sx={{ position: 'absolute', top: 13, left: '12.5%', right: '12.5%', height: 3, borderRadius: 2, bgcolor: '#EAECF0' }} />
            <Box sx={{ position: 'absolute', top: 13, left: '12.5%', width: `calc(75% * ${ratio})`, height: 3, borderRadius: 2, bgcolor: color, transition: 'width .5s ease' }} />
            <Stack direction="row" sx={{ position: 'relative' }}>
                {STEPS.map((label, i) => {
                    const done = complete || i < activeIdx;
                    const active = !complete && i === activeIdx;
                    return (
                        <Stack key={label} alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: done ? color : '#fff',
                                border: done ? `2px solid ${color}` : active ? `2px solid ${color}` : '2px solid #D0D5DD',
                                color: done ? '#fff' : active ? color : '#D0D5DD',
                                zIndex: 1, transition: 'all .3s ease',
                            }}>
                                {done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : active ? (
                                    <Box sx={{
                                        width: 9, height: 9, borderRadius: '50%', bgcolor: color,
                                        animation: 'mPulse 1.6s ease-out infinite',
                                        '@keyframes mPulse': {
                                            '0%': { boxShadow: `0 0 0 0 ${color}66` },
                                            '70%': { boxShadow: `0 0 0 7px ${color}00` },
                                            '100%': { boxShadow: `0 0 0 0 ${color}00` },
                                        },
                                    }} />
                                ) : <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#D0D5DD' }} />}
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: 10, sm: 12 }, lineHeight: 1.25, textAlign: 'center', px: 0.5,
                                minHeight: { xs: 26, sm: 'auto' },   // reserve 2 lines so wrapping labels stay aligned
                                fontWeight: done || active ? 600 : 500,
                                color: done || active ? INK : MUTE,
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
}

function CopyValue({ value, sx }) {
    if (!value) return <Typography variant="body2" sx={{ color: MUTE }}>—</Typography>;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: INK, letterSpacing: 0.3, ...sx }}>{value}</Typography>
            <CopyIconButton value={value} color={MUTE} size={14} />
        </Stack>
    );
}

function Field({ label, children }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: MUTE, mb: 0.5 }}>{label}</Typography>
            {typeof children === 'string'
                ? <Typography sx={{ fontSize: 15, fontWeight: 600, color: INK }}>{children || '—'}</Typography>
                : children}
        </Box>
    );
}

function SectionCard({ title, children }) {
    return (
        <Paper elevation={0} sx={{
            borderRadius: 4, p: { xs: 2.5, sm: 3 }, bgcolor: '#fff',
            border: `1px solid ${HAIR}`, boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.05)',
        }}>
            {title && (
                <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: MUTE, mb: 2 }}>{title}</Typography>
            )}
            {children}
        </Paper>
    );
}

export default function Modern({ brand, order, preview, children }) {
    const data = order || {};
    const hasOrder = !!data.createdDate;
    const pal = getReadableAccent(brand && brand.bannerBgColor);
    const progress = getProgress(data.currentStatus);
    const arrival = arrivalLabel(data);
    const trackColor = progress.tone === 'accent' ? pal.accentText : TONE[progress.tone];
    const heroDate = progress.phase === 'NORMAL' || progress.phase === 'DELIVERED';
    const history = Array.isArray(data.order_history) ? data.order_history : [];
    const [showAll, setShowAll] = React.useState(false);

    // EXTEND the app theme (merge-callback form) instead of replacing it — a bare createTheme here
    // would strip theme.js's component overrides (Paper/Button/TextField/Dialog radii, shadows)
    // from the shared organs rendered inside (SearchBar, OTP dialog, timeline).
    const accentText = pal.accentText;
    const theme = React.useMemo(
        () => (outerTheme) => createTheme(outerTheme, {
            typography: { fontFamily: 'Poppins, sans-serif' },
            palette: { primary: { main: accentText } },
        }),
        [accentText]
    );

    const logo = brand && brand.logo;
    const name = (brand && brand.name) || 'Your Brand';
    const webUrl = toAbsoluteUrl(brand && brand.webUrl);
    const showBanner = hasBrandBanner(brand);
    const showTracker = progress.phase !== 'CANCELLED' && progress.phase !== 'LOST';

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg,#FAFBFC 0%,#F4F6F8 100%)' }}>
                {/* Header — full-width brand banner if provided, else a slim logo bar */}
                {showBanner ? (
                    <BrandBanner brand={brand} />
                ) : (
                    <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', borderBottom: `1px solid ${HAIR}` }}>
                        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                            {/* color+textDecoration guard the anchor case: a bare <a> paints UA blue/underline */}
                            <Box sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
                                {...(webUrl
                                    ? { component: 'a', href: webUrl, target: '_blank', rel: 'noreferrer' }
                                    : { component: 'div' })}>
                                {logo
                                    ? <Box component="img" src={logo} alt={name} sx={{ height: { xs: 26, sm: 32 }, maxWidth: { xs: 170, sm: 280 }, objectFit: 'contain', display: 'block' }} />
                                    : <Typography sx={{ fontWeight: 600, fontSize: 18, color: INK }}>{name}</Typography>}
                            </Box>
                            <PoweredBy logoHeight={16} labelSx={{ fontSize: 11, color: MUTE, fontWeight: 400 }} />
                        </Toolbar>
                    </AppBar>
                )}

                <Box>
                    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
                        <Stack spacing={{ xs: 2.5, sm: 3 }}>
                            {/* OTP verification gate — same shared component as the current page (renders only for NDR) */}
                            <UserVerification />
                            {/* Same "← Track another order" affordance as the current page (hidden in preview) */}
                            {hasOrder && <Box><TrackAnotherButton preview={preview} /></Box>}
                            {/* Center swaps by state: tracking body when an order is loaded, else the
                                search box / not-found passed as children — header/footer stay constant. */}
                            {hasOrder ? (
                                <>
                            {/* HERO */}
                            <Paper elevation={0} sx={{
                                position: 'relative', overflow: 'hidden', borderRadius: 6, p: { xs: 3, sm: 4 }, bgcolor: '#fff',
                                border: `1px solid ${HAIR}`, boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 12px 32px rgba(16,24,40,0.06)',
                            }}>
                                <Box sx={{ position: 'absolute', top: -80, right: -60, width: 220, height: 220, borderRadius: '50%', background: pal.softer, pointerEvents: 'none' }} />
                                <Box sx={{ position: 'relative' }}>
                                    <Chip label={(progress.bucket || '').toUpperCase()} size="small" sx={{
                                        bgcolor: progress.tone === 'accent' ? pal.soft : `${trackColor}1F`,
                                        color: trackColor, fontWeight: 600, fontSize: 11, letterSpacing: 0.6, mb: 1.5,
                                    }} />
                                    <Typography sx={{
                                        fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.5px', color: INK,
                                        fontSize: { xs: 26, sm: 34 },
                                    }}>
                                        {heroDate && arrival.big.startsWith('Arriving')
                                            ? <>Arriving <Box component="span" sx={{ color: pal.accentText }}>{arrival.big.replace('Arriving ', '').replace('Arriving', '').trim() || 'soon'}</Box></>
                                            : arrival.big}
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, color: SUB, mt: 0.5 }}>
                                        {arrival.sub || `Your order from ${name} ${progress.phase === 'DELIVERED' ? 'has been delivered.' : 'is on the way.'}`}
                                    </Typography>

                                    {showTracker && (
                                        <Box sx={{ mt: { xs: 3, sm: 4 } }}>
                                            <ProgressTracker activeIdx={Math.min(progress.step, 3)} complete={progress.phase === 'DELIVERED'} color={trackColor} />
                                        </Box>
                                    )}
                                    {data.currentLocation && progress.phase === 'NORMAL' && (
                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 2.5, color: SUB }}>
                                            <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
                                            <Typography sx={{ fontSize: 13 }}>Currently at {data.currentLocation}</Typography>
                                        </Stack>
                                    )}
                                    {/* Cancel / Re-attempt — same shared component as the current page (renders only for NDR + OTP verified) */}
                                    <Box sx={{ mt: 2.5, '&:empty': { display: 'none' } }}><Action /></Box>
                                </Box>
                            </Paper>

                            {/* AWB + courier pills */}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <SectionCard><Field label="AWB / Tracking No."><CopyValue value={data.waybill} /></Field></SectionCard>
                                <SectionCard>
                                    <Field label="Courier Partner">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: pal.accentText }} />
                                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: INK }}>{data.courierPartnerParent || '—'}</Typography>
                                        </Stack>
                                    </Field>
                                </SectionCard>
                            </Stack>

                            {/* Order details */}
                            <SectionCard title="Order details">
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                    <Field label="Order ID">{(data.shippingLabelId || '').toUpperCase() || '—'}</Field>
                                    <Field label="Placed on">{formatDate(data.createdDate) || '—'}</Field>
                                    <Field label="Payment">
                                        <Chip size="small" icon={<PaymentsOutlinedIcon sx={{ fontSize: 15 }} />} label={(data.paymentMode || '—').toUpperCase()}
                                            sx={{
                                                fontWeight: 600, fontSize: 11,
                                                bgcolor: (data.paymentMode || '').toUpperCase() === 'COD' ? '#FFF4E5' : pal.soft,
                                                color: (data.paymentMode || '').toUpperCase() === 'COD' ? '#B25E09' : pal.accentStrong,
                                                '& .MuiChip-icon': { color: 'inherit' },
                                            }} />
                                    </Field>
                                    <Field label="Invoice value">
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <ReceiptLongOutlinedIcon sx={{ fontSize: 17, color: MUTE }} />
                                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: INK }}>{formatINR(data.invoiceValue) || '—'}</Typography>
                                        </Stack>
                                    </Field>
                                </Box>
                                {(data.pickupLocation || data.deliveryLocation) && (
                                    <>
                                        <Divider sx={{ my: 2.5, borderColor: HAIR }} />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${MUTE}`, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 11, color: MUTE, fontWeight: 600, letterSpacing: 0.5 }}>FROM</Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>{(data.pickupLocation || '—').toUpperCase()}</Typography>
                                            </Box>
                                            <Box sx={{ flex: '0 0 40px', borderTop: `2px dashed ${pal.ring}`, display: { xs: 'none', sm: 'block' } }} />
                                            <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography sx={{ fontSize: 11, color: MUTE, fontWeight: 600, letterSpacing: 0.5 }}>TO</Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>{(data.deliveryLocation || '—').toUpperCase()}</Typography>
                                            </Box>
                                            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: pal.accentText, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
                                        </Stack>
                                    </>
                                )}
                            </SectionCard>

                            {/* Shipment journey */}
                            {history.length > 0 && (
                                <SectionCard title="Shipment journey">
                                    <Box sx={{ position: 'relative' }}>
                                        <Collapse in={showAll || history.length <= 4} collapsedSize={300}>
                                            <OrderTimeline orderDetailInfo={data} />
                                        </Collapse>
                                        {!showAll && history.length > 4 && (
                                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(180deg,rgba(255,255,255,0) 0%,#fff 90%)', pointerEvents: 'none' }} />
                                        )}
                                    </Box>
                                    {history.length > 4 && (
                                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                                            <Button size="small" onClick={() => setShowAll((s) => !s)} sx={{ textTransform: 'none', color: pal.accentStrong, fontWeight: 600 }}>
                                                {showAll ? 'Show less' : 'Show full history'}
                                            </Button>
                                        </Box>
                                    )}
                                </SectionCard>
                            )}
                                </>
                            ) : children}
                        </Stack>
                    </Container>
                </Box>

                {/* Footer */}
                <Box component="footer" sx={{ borderTop: `1px solid ${HAIR}`, bgcolor: '#F9FAFB', py: 4, px: 2, textAlign: 'center' }}>
                    <Stack spacing={1.5} alignItems="center">
                        {logo
                            ? <Box component="img" src={logo} alt={name} sx={{ height: 26, maxWidth: 220, objectFit: 'contain', opacity: 0.9 }} />
                            : <Typography sx={{ fontWeight: 600, color: INK }}>{name}</Typography>}
                        {brand && brand.footer && (
                            <Typography sx={{ fontSize: 13, color: SUB, maxWidth: 520, whiteSpace: 'pre-wrap' }}>{brand.footer}</Typography>
                        )}
                        <SocialRow brand={brand} color={MUTE} hoverColor={pal.accentText} size={20} />
                        <PolicyLinks brand={brand} color={MUTE} size={12} />
                        <PoweredBy logoHeight={18} labelSx={{ fontSize: 11, color: MUTE, fontWeight: 400 }} />
                    </Stack>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
